import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Tv, Loader2, UserPlus } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import SocialAuth from "@/components/SocialAuth";
import { useTranslation } from "react-i18next";

const BG = "https://images.unsplash.com/photo-1572188863110-46d457c9234d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Register() {
  const nav = useNavigate();
  const { user, authenticate } = useAuth();
  const { code } = useParams();
  const [inviter, setInviter] = useState(null);
  const { display: priceDisplay, freeAccess } = useCurrency();
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!code) return;
    api.get(`/invite/${code}`)
      .then(({ data }) => { setInviter(data.name); localStorage.setItem("ntv_ref", data.code); })
      .catch(() => setInviter(false));
  }, [code]);

  useEffect(() => { if (code && user) nav("/"); }, [code, user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { name, email, password });
      authenticate(data.token, data.user);
      nav(data.user?.subscribed ? "/" : "/subscribe");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Registration failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen relative grid place-items-center px-4" data-testid="register-page">
      <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] to-[#0A0A0C]/60" />
      <div className="relative z-10 w-full max-w-md backdrop-blur-xl bg-black/60 border border-white/10 rounded-2xl p-8 shadow-2xl animate-fade-up">
        <Link to="/" className="flex items-center gap-2 justify-center mb-6">
          <Tv className="text-brand" size={28} />
          <span className="font-head font-extrabold text-2xl">NeTime<span className="text-brand">TV</span></span>
        </Link>
        {inviter && (
          <div data-testid="invite-heading" className="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-emerald-200 text-sm font-semibold">
            <UserPlus size={16} className="shrink-0" /> {t("invite.heading", { name: inviter })}
          </div>
        )}
        {inviter === false && <p data-testid="invite-invalid" className="mb-4 text-xs text-zinc-400">{t("invite.invalid")}</p>}
        <h1 className="font-head text-2xl font-bold mb-1">Create your account</h1>
        {freeAccess ? (
          <p data-testid="register-free-copy" className="text-zinc-400 text-sm mb-6">{t("free.registerBody")}</p>
        ) : (
          <p className="text-zinc-400 text-sm mb-6">Get <span className="text-gold font-semibold">24 hours free</span> the moment you sign up — then {priceDisplay}/mo for 5,000+ live HD channels & games.</p>
        )}
        {error && <div data-testid="register-error" className="bg-brand/20 border border-brand/40 text-sm rounded-lg px-4 py-2 mb-4">{error}</div>}
        <form onSubmit={submit} className="space-y-4">
          <input data-testid="register-name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Name"
            className="w-full bg-white/5 border border-white/15 rounded-lg px-4 py-3 outline-none focus:border-brand transition-colors" />
          <input data-testid="register-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email"
            className="w-full bg-white/5 border border-white/15 rounded-lg px-4 py-3 outline-none focus:border-brand transition-colors" />
          <input data-testid="register-password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password (min 6 chars)"
            className="w-full bg-white/5 border border-white/15 rounded-lg px-4 py-3 outline-none focus:border-brand transition-colors" />
          <button data-testid="register-submit" disabled={loading}
            className="w-full bg-brand hover:bg-brand-hover font-bold py-3 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
            {loading && <Loader2 className="animate-spin" size={18} />} {freeAccess ? t("free.registerBtn") : "Create Account & Subscribe"}
          </button>
        </form>
        <div className="mt-5"><SocialAuth /></div>
        <p className="text-sm text-zinc-400 mt-6 text-center">
          Already a member? <Link to="/login" className="text-white font-semibold hover:text-brand">Sign In</Link>
        </p>
      </div>
    </div>
  );
}
